import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Box, Button, Chip, colors, Stack, Toolbar, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Lock as LockIcon } from '@mui/icons-material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import parse from 'html-react-parser';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useConfirm } from 'material-ui-confirm';

import { DraftManualDetailContext } from './context';
import { AppContext } from 'views/App/context';
import { ServerErrorResponse } from 'data_sources/constants';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import errorHelper from 'helpers/errorHelper';
import datetimeHelper from 'helpers/datetimeHelper';
import manualHelper from '../helpers/draftManualHelper';
import draftManualHelper from '../helpers/draftManualHelper';
import useManageChapter from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageChapter';
import useManagePreface from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManagePreface';
import useManagePolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManagePolicy';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';
import { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import { neutral, primary, success, warning } from '_lex/_foundations/phxColors';
import { StyledBadge, StyledChip } from './styles';
import LeftDrawer from '_lex/_layouts/TakeoverLayout/LeftDrawer';

const draftStateChipStyles: Record<string, { backgroundColor: string; color: string }> = {
  DRAFT: { backgroundColor: neutral[100], color: neutral[700] },
  IN_APPROVAL: { backgroundColor: warning[50], color: warning[800] },
  APPROVED: { backgroundColor: success[50], color: success[800] },
  ISSUED: { backgroundColor: primary[50], color: primary[800] },
};

const ToC: React.FC = () => {
  const { currentProfile } = useContext(AppContext);
  const {
    manualHasPermissions,
    manual,
    pendingUpdates,
    enabledFilter,
    disabledFilter,
    node,
    setNode,
    tocExpandedItems,
    setTocExpandedItems,
  } = useContext(DraftManualDetailContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [isCreating, setIsCreating] = useState(false);
  const [gettingLock, setGettingLock] = useState<boolean>(false);
  const [lastScrolledNode, setLastScrolledNode] = useState<string | null>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { create: createChapter } = useManageChapter();
  const { create: createPreface } = useManagePreface();
  const { create: createPolicy } = useManagePolicy();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { getLock, removeLock, takeLock } = useManageManual();

  const currentUserId = currentProfile?.user?.id;
  const isManualLocked = manual.isLocked;
  const manualLockOwnerId = manual.lock?.userId;
  const isLockedByCurrentUser = isManualLocked && manualLockOwnerId === currentUserId;

  const expandNodes = (ids: string[]) => {
    const current = tocExpandedItems ?? [];
    const nextSet = new Set(current);
    ids.forEach((x) => nextSet.add(x));
    const next = Array.from(nextSet);
    if (next.length !== current.length || ids.some((x) => !current.includes(x))) {
      setTocExpandedItems(next);
    }
  };

  const collapseNodes = (ids: string[]) => {
    const current = tocExpandedItems ?? [];
    const next = current.filter((x) => !ids.includes(x));
    if (next.length !== current.length) {
      setTocExpandedItems(next);
    }
  };

  const handleExpandedItemsChange = (_event: React.SyntheticEvent | null, itemIds: string[]) => {
    const next = Array.isArray(itemIds) ? itemIds : [];
    setTocExpandedItems(next);
  };

  useEffect(() => {
    if (node && node !== lastScrolledNode) {
      const [type, id] = node.split(':');

      // If it's a policy, find and expand the parent chapter first
      if (type === 'policy') {
        const policy = manual?.chapters?.flatMap((chapter) => chapter.policies)?.find((p) => p.id === id);

        if (policy) {
          const parentChapter = manual?.chapters?.find((chapter) => chapter.policies?.some((p) => p.id === policy.id));

          if (parentChapter) {
            expandNodes([parentChapter.id]);
          }
        }
      }

      // Ensure the selected item's parent is expanded
      const selectedId = selectedItem(node);
      if (selectedId) {
        const parentId = getParentId(selectedId);
        if (parentId) {
          expandNodes([parentId]);
        }

        // Use a combination of setTimeout and requestAnimationFrame to ensure DOM is ready
        const scrollToElement = (retryCount = 0) => {
          const element = document.getElementById(`tree-item-${selectedId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setLastScrolledNode(node);
          } else if (retryCount < 10) {
            // Maximum 10 retries - if element not found, try again after a short delay
            scrollTimeoutRef.current = setTimeout(() => scrollToElement(retryCount + 1), 100);
          }
        };

        // Initial attempt after a short delay to allow for tree rendering
        scrollTimeoutRef.current = setTimeout(() => {
          requestAnimationFrame(() => scrollToElement(0));
        }, 200);
      }
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = undefined;
      }
    };
  }, [manual, node, lastScrolledNode]);

  const getParentId = (id: string): string | null => {
    // Check if it's a policy
    const policy = manual?.chapters?.flatMap((chapter) => chapter.policies)?.find((p) => p.id === id);
    if (policy) {
      const parentChapter = manual?.chapters?.find((chapter) => chapter.policies?.some((p) => p.id === policy.id));
      return parentChapter?.id || null;
    }
    return null;
  };

  const handleNodeClick = (type: string, nodeId: string) => {
    setNode(`${type}:${nodeId}`);
  };

  const selectedItem = (node?: string) => {
    if (!node) return null;

    const [type, id] = node.split(':');
    return id;
  };

  const handleCreateChapter = () => {
    if (isManualLocked && !isLockedByCurrentUser) {
      return enqueueSnackbar(t('draftManuals.toc.lockedBy', '', { username: manual?.lock?.username || '' }));
    }
    confirm({
      title: t('draftManuals.addConfirmation.chapter.title'),
      description: t('draftManuals.addConfirmation.chapter.message'),
      confirmationText: t('draftManuals.addConfirmation.chapter.confirm'),
      cancellationText: t('draftManuals.addConfirmation.cancel'),
      confirmationButtonProps: {
        variant: 'contained',
      },
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
    })
      .then(() => {
        setIsCreating(true);
        createChapter(
          manual.id,
          { title: 'Chapter Title' },
          (chapterId) => {
            queryClient.invalidateQueries(['draft_manual', manual.id]);
            setIsCreating(false);
            handleNodeClick('chapter', chapterId);
          },
          (error) => {
            setIsCreating(false);
            errorHelper.enqueueErrors(error);
          },
        );
      })
      .catch(() => {
        // User cancelled
      });
  };

  const handleCreatePreface = () => {
    if (isManualLocked && !isLockedByCurrentUser) {
      return enqueueSnackbar(t('draftManuals.toc.lockedBy', '', { username: manual?.lock?.username || '' }));
    }
    confirm({
      title: t('draftManuals.addConfirmation.preface.title'),
      description: t('draftManuals.addConfirmation.preface.message'),
      confirmationText: t('draftManuals.addConfirmation.preface.confirm'),
      cancellationText: t('draftManuals.addConfirmation.cancel'),
      confirmationButtonProps: {
        variant: 'contained',
      },
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
    })
      .then(() => {
        setIsCreating(true);
        expandNodes(['prefaces']);
        createPreface(
          manual.id,
          { title: 'Preface Title' },
          (preface) => {
            queryClient.invalidateQueries(['draft_manual', manual.id]);
            queryClient.invalidateQueries(['draft_preface', manual.id]);
            setIsCreating(false);
            handleNodeClick('preface', preface.id);
          },
          (error) => {
            setIsCreating(false);
            errorHelper.enqueueErrors(error);
          },
        );
      })
      .catch(() => {
        // User cancelled
      });
  };

  const handleCreatePolicy = (chapterId: string) => {
    if (isManualLocked && !isLockedByCurrentUser) {
      return enqueueSnackbar(t('draftManuals.toc.lockedBy', '', { username: manual?.lock?.username || '' }));
    }
    confirm({
      title: t('draftManuals.addConfirmation.policy.title'),
      description: t('draftManuals.addConfirmation.policy.message'),
      confirmationText: t('draftManuals.addConfirmation.policy.confirm'),
      cancellationText: t('draftManuals.addConfirmation.cancel'),
      confirmationButtonProps: {
        variant: 'contained',
      },
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
    })
      .then(() => {
        setIsCreating(true);
        expandNodes([chapterId]);
        createPolicy(
          manual.id,
          { chapterId, title: 'Policy Title' },
          (policy) => {
            queryClient.invalidateQueries(['draft_manual', manual.id]);
            queryClient.invalidateQueries(['draft_policy', manual.id]);
            handleNodeClick('policy', policy.id);
            setIsCreating(false);
          },
          (error) => {
            setIsCreating(false);
            errorHelper.enqueueErrors(error);
          },
        );
      })
      .catch(() => {
        // User cancelled
      });
  };

  const handleManageStructureClick = () => {
    setGettingLock(true);
    if (!isManualLocked) {
      getLock(
        manual.id,
        async (lock) => {
          queryClient.invalidateQueries(['draft_manual', manual.id]);
          await queryClient.fetchQuery(['draft_manual', manual.id]);
          setGettingLock(false);
          navigate(`/admin/policy/draft-manuals/${manual.id}/manual-structure`);
        },
        (error: ServerErrorResponse) => {
          errorHelper.enqueueErrors(error);
          setGettingLock(false);
        },
      );
    } else if (isLockedByCurrentUser) {
      setGettingLock(false);
      navigate(`/admin/policy/draft-manuals/${manual.id}/manual-structure`);
    }
  };
  const setActionsMenuItems = () => {
    const actions: ActionMenuItemProps[] = [];
    if (manualHasPermissions(manual.id, ['updateDraftManual'])) {
      actions.push({
        key: t('common.manageStructure'),
        label: t('common.manageStructure'),
        icon: isManualLocked && !isLockedByCurrentUser && (
          <Tooltip
            title={t('draftManuals.manualStructure.lockedBy') + manual.lock?.username}
            sx={{ marginTop: 'auto', marginBottom: 'auto', height: 20 }}
          >
            <LockIcon color='neutral' />
          </Tooltip>
        ),
        onClick: () => handleManageStructureClick(),
        disabled: (isManualLocked && !isLockedByCurrentUser) || gettingLock,
      });
    }
    actions.push({
      key: t('draftManuals.attachmentLibrary.title'),
      label: t('draftManuals.attachmentLibrary.title'),
      onClick: () => setShowDialog(true),
    });
    actions.push({
      key: t('draftManuals.toc.releaseNotesAndGuideSheets'),
      label: t('draftManuals.toc.releaseNotesAndGuideSheets'),
      href: 'https://lexipolllc.sharepoint.com/:w:/s/PHXReleaseNotesandGuides/EaVTcfFolDZMrESoEID4BJwBKZOhMaPVbhUezHSznsayGQ?e=6NBhf8',
      btnProps: {
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    });
    return actions;
  };

  useEffect(() => {
    if (dayjs(manual.lock?.createdAt).isBefore(dayjs().subtract(20, 'minute'))) {
      if (isLockedByCurrentUser) {
        removeLock(
          manual.id,
          () => {
            queryClient.invalidateQueries(['draft_manual', manual.id]);
          },
          (error: ServerErrorResponse) => {
            errorHelper.enqueueErrors(error);
          },
        );
      } else {
        {
          takeLock(
            manual.id,
            () => {
              removeLock(
                manual.id,
                () => {
                  queryClient.invalidateQueries(['draft_manual', manual.id]);
                },
                (error: ServerErrorResponse) => {
                  errorHelper.enqueueErrors(error);
                },
              );
            },
            (error: ServerErrorResponse) => {
              errorHelper.enqueueErrors(error);
            },
          );
        }
      }
    }
  }, [manual]);

  // Filter function for ToC items - shared across all item types
  const shouldShowItem = useMemo(
    () => (item: { state: string; isNew?: boolean }) => {
      // Always show new items regardless of filter
      if (item.isNew) return true;

      // If both filters are enabled, show everything
      if (enabledFilter && disabledFilter) return true;

      // If only enabled filter is on, show only enabled items
      if (enabledFilter && !disabledFilter) return item.state === 'ENABLED';

      // If only disabled filter is on, show only disabled items
      if (disabledFilter && !enabledFilter) return item.state === 'DISABLED';

      // If both are off (shouldn't happen due to toggle logic, but handle it)
      return false;
    },
    [enabledFilter, disabledFilter],
  );

  const filteredPrefaces = useMemo(
    () => (manual?.prefaces ?? []).filter(shouldShowItem),
    [manual?.prefaces, shouldShowItem],
  );

  const filteredChapters = useMemo(() => {
    const shouldShowChapter = (chapter: { state: string; policies?: Array<{ state: string; isNew?: boolean }> }) => {
      // If both filters are enabled, show everything
      if (enabledFilter && disabledFilter) return true;

      // Check if chapter itself matches filter
      const chapterMatches = shouldShowItem(chapter);

      // Check if any policy in the chapter matches the filter
      const hasMatchingPolicy = chapter.policies?.some((policy) => shouldShowItem(policy));

      // Show chapter if it matches OR if it has a matching policy
      return chapterMatches || hasMatchingPolicy;
    };

    return (manual?.chapters ?? []).filter(shouldShowChapter);
  }, [manual?.chapters, shouldShowItem, enabledFilter, disabledFilter]);

  const renderContent = () => (
    <SimpleTreeView
      selectedItems={selectedItem(node)}
      expandedItems={[...(tocExpandedItems ?? [])]}
      sx={{
        '& .MuiTreeItem-content.Mui-selected': {
          scrollMarginTop: '50px',
          scrollMarginBottom: '50px',
        },
      }}
      onExpandedItemsChange={handleExpandedItemsChange}
      expansionTrigger='content'
    >
      <TreeItem
        key={'prefaces'}
        itemId={'prefaces'}
        id={`tree-item-prefaces`}
        label={
          <Toolbar className='phx-tree-toolbar '>
            <Stack
              direction='row'
              spacing={4}
              sx={{ flexGrow: 1 }}
            >
              <Typography>{t('draftManuals.toc.prefaces')}</Typography>
              {!_.isEmpty(pendingUpdates) && pendingUpdates.prefacesUpdated > 0 && (
                <StyledBadge
                  badgeContent={pendingUpdates.prefacesUpdated}
                  color='error'
                />
              )}
            </Stack>
            <Stack
              direction='row'
              spacing={4}
            >
              {manualHasPermissions(manual.id, ['createDraftPreface']) && (
                <Tooltip
                  title={t('draftManuals.toc.addPreface')}
                  placement='left'
                >
                  <Button
                    className='icon-only'
                    variant='outlined'
                    color='neutral'
                    size='small'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreatePreface();
                    }}
                    startIcon={<AddIcon />}
                    disabled={isCreating}
                  />
                </Tooltip>
              )}
            </Stack>
          </Toolbar>
        }
        sx={{
          '& .MuiTreeItem-content': {
            borderBottom: `solid 1px ${colors.grey[300]}`,
            py: 3,
          },
        }}
      >
        {filteredPrefaces.map((preface) => (
          <TreeItem
            key={preface.id}
            itemId={preface.id}
            id={`tree-item-${preface.id}`}
            label={
              <Stack spacing={1}>
                <Stack
                  direction='row'
                  spacing={2}
                >
                  <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {parse(preface.title)}
                  </Typography>
                  {!_.isEmpty(pendingUpdates) &&
                    pendingUpdates.prefaces[preface.id] &&
                    pendingUpdates.prefaces[preface.id].count > 0 && (
                      <StyledChip
                        label={
                          pendingUpdates &&
                          t(
                            pendingUpdates.prefaces[preface.id].count === 1
                              ? 'draftManuals.pendingUpdates.singularLabel'
                              : 'draftManuals.pendingUpdates.label',
                            '',
                            {
                              count: pendingUpdates.prefaces[preface.id].count.toString(),
                            },
                          )
                        }
                        variant='outlined'
                      />
                    )}
                  {preface.isNew && (
                    <StyledChip
                      label={t('draftManuals.toc.new')}
                      variant='outlined'
                      color='success'
                    />
                  )}
                  {preface.lock && (
                    <Stack
                      direction='row'
                      spacing={1}
                    >
                      {draftManualHelper.prefaceLockAvatar(preface, true) ?? <Box />}
                      <LockIcon
                        color='neutral'
                        fontSize='small'
                      />
                    </Stack>
                  )}
                </Stack>
                <Stack
                  direction='row'
                  spacing={1}
                >
                  <Chip label={t(`draftManuals.draftState.${preface.draftState}`)} />
                  <Typography variant='caption'>
                    {preface.issuedAt
                      ? `${t('draftManuals.toc.issued')}: ${datetimeHelper.formatDate(preface.issuedAt)}`
                      : t('draftManuals.toc.notIssued')}
                  </Typography>
                </Stack>
              </Stack>
            }
            onClick={() => handleNodeClick('preface', preface.id)}
            sx={{
              opacity: preface.state === 'ENABLED' || (preface.isNew && preface.state === 'DISABLED') ? 1 : 0.5,
              '& .MuiTreeItem-content': {
                borderBottom: `solid 1px ${colors.grey[300]}`,
                py: 3,
              },
            }}
          />
        ))}
      </TreeItem>
      {filteredChapters.map((chapter) => (
        <TreeItem
          key={chapter.id}
          itemId={chapter.id}
          id={`tree-item-${chapter.id}`}
          label={
            <Toolbar className='phx-tree-toolbar' sx={{ gap: 2 }}>
              <Stack
                direction='row'
                spacing={4}
                sx={{
                  flexGrow: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {manualHelper.formatManualNodeTitle(chapter, manual)}
                </Typography>
                {!_.isEmpty(pendingUpdates) &&
                  pendingUpdates.chapters[chapter.id] &&
                  pendingUpdates.chapters[chapter.id] > 0 && (
                    <StyledBadge
                      badgeContent={pendingUpdates.chapters[chapter.id]}
                      color='error'
                    />
                  )}
                <span data-updates={JSON.stringify(pendingUpdates)}></span>
                {!chapter.jurisdictionChapterId?.length && (
                  <Chip
                    label={t('draftManuals.editLevelTag.AGENCY_CONTENT')}
                    variant='outlined'
                    size='small'
                    color='neutral'
                  />
                )}
              </Stack>
              <Stack
                direction='row'
                spacing={4}
              >
                {manualHasPermissions(manual.id, ['createDraftPolicy']) && (
                  <Tooltip
                    title={t('draftManuals.toc.addPolicy')}
                    placement='left'
                  >
                    <Button
                      className='icon-only'
                      variant='outlined'
                      color='neutral'
                      size='small'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreatePolicy(chapter.id);
                      }}
                      startIcon={<AddIcon />}
                      disabled={isCreating}
                    />
                  </Tooltip>
                )}
              </Stack>
            </Toolbar>
          }
          onClick={() => handleNodeClick('chapter', chapter.id)}
          sx={{
            opacity: chapter.state === 'ENABLED' ? 1 : 0.5,
            '& .MuiTreeItem-content': {
              borderBottom: `solid 1px ${colors.grey[300]}`,
              py: 3,
            },
          }}
        >
          {(chapter.policies ?? []).filter(shouldShowItem).map((policy) => (
            <TreeItem
              key={policy.id}
              itemId={policy.id}
              id={`tree-item-${policy.id}`}
              label={
                <Stack spacing={1}>
                  <Stack
                    direction='row'
                    spacing={3}
                  >
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {manualHelper.formatManualNodeTitle(policy, manual)}
                    </Typography>
                    {!_.isEmpty(pendingUpdates) &&
                      pendingUpdates.policies[policy.id] &&
                      pendingUpdates.policies[policy.id].count > 0 &&
                      !pendingUpdates.policies[policy.id].isNewPolicyOnly && (
                        <StyledChip
                          label={t(
                            pendingUpdates.policies[policy.id].count === 1
                              ? 'draftManuals.pendingUpdates.singularLabel'
                              : 'draftManuals.pendingUpdates.label',
                            '',
                            {
                              count: pendingUpdates.policies[policy.id].count.toString(),
                            },
                          )}
                          variant='outlined'
                        />
                      )}
                    {policy.isNew && (
                      <StyledChip
                        label={t('draftManuals.toc.new')}
                        variant='outlined'
                        color='success'
                      />
                    )}
                    {policy.lock && (
                      <Stack
                        direction='row'
                        spacing={1}
                      >
                        {draftManualHelper.policyLockAvatar(policy, true) ?? <Box />}
                        <LockIcon
                          color='neutral'
                          fontSize='small'
                        />
                      </Stack>
                    )}
                  </Stack>

                  <Stack
                    direction='row'
                    spacing={1}
                  >
                    <Chip
                      label={t(`draftManuals.draftState.${policy.draftState}`)}
                      sx={draftStateChipStyles[policy.draftState] ?? draftStateChipStyles.DRAFT}
                    />
                    <Typography variant='caption'>
                      {policy.issuedAt
                        ? `${t('draftManuals.toc.issued')}: ${datetimeHelper.formatDate(policy.issuedAt)}`
                        : t('draftManuals.toc.notIssued')}
                    </Typography>
                    {policy.isAgencyContent &&
                      <Chip
                        label={t('draftManuals.editLevelTag.AGENCY_CONTENT')}
                        variant='outlined'
                        size='small'
                        color='neutral'
                      />
                    }
                  </Stack>
                </Stack>
              }
              onClick={() => handleNodeClick('policy', policy.id)}
              sx={{
                opacity: policy.state === 'ENABLED' || (policy.isNew && policy.state === 'DISABLED') ? 1 : 0.5,
                '& .MuiTreeItem-content': {
                  borderBottom: `solid 1px ${colors.grey[300]}`,
                  py: 3,
                },
              }}
            />
          ))}
        </TreeItem>
      ))}
    </SimpleTreeView>
  );

  const renderHeader = () => (
    <Stack
      direction='row'
      spacing={4}
      sx={{ flexGrow: 1 }}
    >
      <Typography variant='subtitle1'>{t('draftManuals.toc.content')}</Typography>
    </Stack>
  );

  const renderHeaderActions = () => (
    <Stack
      direction='row'
      spacing={4}
    >
      {manualHasPermissions(manual.id, ['createDraftManualChapter']) && (
        <Tooltip
          title={t('draftManuals.toc.addChapter')}
          placement='left'
        >
          <Button
            className='icon-only'
            variant='outlined'
            color='neutral'
            size='small'
            onClick={handleCreateChapter}
            startIcon={<AddIcon />}
            disabled={isCreating}
          />
        </Tooltip>
      )}
    </Stack>
  );

  return manual ? (
    <LeftDrawer
      header={renderHeader()}
      headerActions={renderHeaderActions()}
      content={renderContent()}
      open={true}
      noPadding
    />
  ) : null;
};

export default ToC;
